import React, { useState, useEffect } from 'react';
import { API_BASE_URL, formatEuri } from '../config/constants';

export default function Expenses({ selectedProjectID }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    let fetchPromise;
    
    // Ako je odabrano "Sve", spajamo troškove s više gradilišta
    if (selectedProjectID === 'Sve') {
      fetchPromise = Promise.all([
        fetch(`${API_BASE_URL}/expenseList?projectID=1`, { headers }).then(res => res.json().catch(() => [])),
        fetch(`${API_BASE_URL}/expenseList?projectID=2`, { headers }).then(res => res.json().catch(() => []))
      ]).then(([p1, p2]) => {
        const merged = [];
        const all = [...p1, ...p2];
        
        all.forEach(item => {
          const existing = merged.find(s => s.category === item.category);
          if (existing) {
            existing.plannedAmount = String(parseFloat(existing.plannedAmount || 0) + parseFloat(item.plannedAmount || 0));
            existing.actualAmount = String(parseFloat(existing.actualAmount || 0) + parseFloat(item.actualAmount || 0));
          } else {
            merged.push({ ...item });
          }
        });
        return merged;
      });
    } else {
      fetchPromise = fetch(`${API_BASE_URL}/expenseList?projectID=${selectedProjectID}`, { headers }).then(res => res.json());
    }

    fetchPromise
      .then(data => {
        setExpenses(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error("Greška pri dohvaćanju troškovnika:", err);
      })
      .finally(() => setLoading(false));
  }, [selectedProjectID]);

  // Računanje ukupnih vrijednosti
  const totalPlanned = expenses.reduce((sum, item) => sum + parseFloat(item.plannedAmount || 0), 0);
  const totalSpent = expenses.reduce((sum, item) => sum + parseFloat(item.actualAmount || 0), 0);
  const remainingBudget = totalPlanned - totalSpent;
  const spentPercent = totalPlanned > 0 ? Math.round((totalSpent / totalPlanned) * 100) : 0;

  return (
    <div className="max-w-6xl space-y-6 pb-12">
      
      {/* 1. ZAGLAVLJE */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Troškovnik projekta</h1>
        
        <span className="text-xs font-bold bg-white border border-slate-200 px-3 py-1.5 rounded-xl text-slate-600 shadow-2xs">
          Kategorija: <strong className="text-slate-900">{expenses.length}</strong>
        </span>
      </div>

      {/* 2. GORNJI RED: 3 GLAVNE KPI KARTICE */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        
        {/* Planirani budžet */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between h-24">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Planirani proračun</span>
          <p className="text-2xl font-extrabold text-slate-900 leading-none">{formatEuri(totalPlanned)}</p>
          <p className="text-xs text-slate-400 font-medium">Ukupno planirani troškovi</p>
        </div>

        {/* Utrošeno */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between h-24">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Utrošeno do sada</span>
          <p className="text-2xl font-extrabold text-slate-900 leading-none">{formatEuri(totalSpent)}</p>
          <p className="text-xs text-slate-500 font-medium">Iskorišteno: <strong className="text-slate-800">{spentPercent}%</strong> proračuna</p>
        </div>

        {/* Preostalo */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between h-24">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Preostali proračun</span>
          <p className={`text-2xl font-extrabold leading-none ${remainingBudget >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {formatEuri(remainingBudget)}
          </p>
          <p className={`text-xs font-bold ${remainingBudget >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
            {remainingBudget >= 0 ? 'Unutar planiranog budžeta' : 'Prekoračenje proračuna'}
          </p>
        </div>

      </div>

      {/* 3. POPIS KATEGORIJA TROŠKOVA S TRAKAMA NAPRETKA */}
      {loading ? (
        <p className="text-slate-400 text-center py-16 text-xs font-semibold">Učitavanje troškovnika...</p>
      ) : (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-6">
          <div className="border-b border-slate-100 pb-3">
            <h2 className="text-sm font-bold text-slate-900">Troškovi po fazama izgradnje</h2>
          </div>

          {expenses.length === 0 ? (
            <p className="text-xs text-slate-400 italic text-center py-8">Nema unesenih stavki troškovnika za ovaj projekt.</p>
          ) : (
            <div className="space-y-5">
              {expenses.map(exp => {
                const planned = parseFloat(exp.plannedAmount || 0);
                const spent = parseFloat(exp.actualAmount || 0);
                const percentage = planned > 0 ? (spent / planned) * 100 : 0;
                const cappedPercent = Math.min(percentage, 100);
                
                let progressColor = 'bg-emerald-500';
                let tagColor = 'text-emerald-700 bg-emerald-50 border-emerald-200';
                
                if (percentage > 85 && percentage <= 100) {
                  progressColor = 'bg-amber-500';
                  tagColor = 'text-amber-700 bg-amber-50 border-amber-200';
                } else if (percentage > 100) {
                  progressColor = 'bg-rose-500';
                  tagColor = 'text-rose-700 bg-rose-50 border-rose-200';
                }

                return (
                  <div key={exp.expenseID} className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-slate-900">{exp.category}</span>
                      
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-600">
                          {formatEuri(spent)} <span className="text-slate-400 font-normal">/ {formatEuri(planned)}</span>
                        </span>
                        
                        <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-md border ${tagColor}`}>
                          {percentage.toFixed(1)}%
                        </span>
                      </div>
                    </div>

                    {/* Traka progresa */}
                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden flex">
                      <div 
                        className={`${progressColor} h-full transition-all duration-500 rounded-full`} 
                        style={{ width: `${cappedPercent}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

    </div>
  );
}